import { getBaseRole } from '@/helpers/customRoles';
import { FC, useEffect, useMemo, useState } from 'react'
import Modal from '@common/Modal'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import classes from './index.module.css'
import { IcoRemove, InfoIcon } from '@/utils/icons'
import Button from '@UI/New/Button'
import TextField from '@pages/Admin/CourseSettings/components/TextField/TextField'
import cn from 'classnames'
import { useAddAlert } from '@/hooks/useAddAlert'
import { directionGroupApi, usersAPI } from '@/api'
import { Form, Switch } from 'antd'
import NewSelect from '@UI/New/Select'
import TeacherSalaryFields, { SalaryFormValues } from '../EditTeacherSalaryModal/TeacherSalaryFields'
import { getColleagueOptions } from '../EditGroupModal/utils/api'
import Preloader from '@UI/Preloader'
import { FormProps, useForm } from 'antd/es/form/Form'
import FormItem from 'antd/es/form/FormItem'
import { useAsyncFn, useDebounce } from 'react-use'
import useRoles from '@/hooks/useRoles'
import { useGetDirection } from '@/hooks/useGetDirection'
import { useGetId } from '@/hooks/useGetId'
import { useGetGroupName } from '@pages/Admin/PlatformSettings2/AddSettingsEmployeesPersonAccount/hooks/useGetGroupName'
import { InputWhite } from '@UI/New/InputWhite'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { useGetAdditionalSettings } from '@pages/Admin/CourseSettings/components/AdditionalCourseSettings/hooks/useGetAdditionalSettings'
import { groupName } from '@pages/Admin/CourseSettings/components/AdditionalCourseSettings/utils/fetchAdditionalSettings'
import classNames from 'classnames'
import { useGetUserProfile } from '@/hooks/useGetUserProfile'
import rolesEnum from '@/constant/rolesEnum'
import useFranchises from '@/hooks/useFranchises'
import Text from '@UI/New/Text'
import {
  isThresholdSalary,
  isValidTeacherStudentRates,
  isValidPaidCancelSalaryPercent,
} from '../../utils/teacherSalary'
import { TeacherSalaryType } from '../../types'
import { useGetCurrentGradeSystem } from '@/hooks/useGetCurrentGradeSystem'

const CompactSelect = (props: any) => {
  const fields = { ...props }
  delete fields.className
  delete fields.popupClassName
  return <NewSelect compact className={classes.compactSelect} fields={fields} />
}

type DirectionSett =
  | {
      directionId: number
      enableCourseSelect?: boolean
    }
  | {
      directionId?: number | null
      enableCourseSelect: true
    }

type CreateGroupProps = DirectionSett & {
  open: boolean
  onClose: () => void
  onSuccess: (groupId: number) => void
  onGroupCreated?: (group: IGroup) => void
  teacherId?: number | null
  defaultLessonDuration?: number
  studentLessonPrice?: number | null
  groupType?: IGroupType | null
  allowedFranchiseIds?: number[]
}

export interface FieldType {
  franchise_id: number | null
  direction_id: number
  teacher_id: number | null
  name: string
  max_students: number | null
  apply_max_students: boolean
  salary_type: TeacherSalaryType
  salary: number | null
  paid_cancel_percent: number | string
  calculate_teacher_salary: boolean
  duration_minutes: number | null
  lesson_duration: number | null
  lessons_per_week?: number | null
  student_lesson_price: number | null
  is_lesson_payment_group: boolean
  is_shared_balance: boolean
  min_amount_enabled: boolean
  min_amount: number | null
  has_additional_teachers: boolean
  additional_teacher_ids: number[]
}

type CreateGroupContentProps = CreateGroupProps & {}

const DEFAULT_SALARY_TYPE: TeacherSalaryType = 'per-lesson'
const GRADING_SYSTEMS = [5, 10, 12, 100]
const GRADING_SYSTEM_OPTIONS = GRADING_SYSTEMS.map((value) => ({
  value,
  label: `${value}-балльная`,
}))
const HIDE_JOURNAL_TOOLTIP =
  'Включая чекбокс данная группа не будет отображаться в основном разделе (посещения) электронного журнала.'
const HIDE_HOMEWORK_JOURNAL_TOOLTIP =
  'Включая чекбокс данная группа не будет отображаться в разделе ДЗ/КР Электронного журнала.'
const ADDITIONAL_TEACHERS_TOOLTIP =
  'Если вы включите данный тумблер, вы сможете добавить любого педагога в качестве дополнительного к этой группе. У дополнительного педагога будут появляться все те же домашние работы, контрольные и пробники, которые задал основной педагог. Дополнительные педагоги смогут проверять эти работы, а также задавать собственные работы в этой группе.'

const normalizeNonNegativeNumberInput = (value: string) => {
  if (!value) return ''
  return value.replace('-', '')
}

const getValidGradingSystem = (value: unknown, fallback = GRADING_SYSTEMS[0]) => {
  const gradingSystem = Number(value)
  return GRADING_SYSTEMS.includes(gradingSystem) ? gradingSystem : fallback
}

const CreateGroupModalContent: FC<CreateGroupContentProps> = ({
  directionId,
  enableCourseSelect,
  open,
  onClose,
  onSuccess,
  onGroupCreated,
  teacherId,
  defaultLessonDuration,
  studentLessonPrice,
  groupType,
  allowedFranchiseIds,
}) => {
  const { groupNamePlural, groupNameSingular } = useGetGroupName()
  const addAlert = useAddAlert()
  const [form] = useForm()
  const platformGradingSystem = useGetCurrentGradeSystem()
  const defaultGradingSystem = getValidGradingSystem(platformGradingSystem)
  const [salaryForm, setSalaryForm] = useState<SalaryFormValues>({ salaryType: 'per-lesson', salary: '0', salaryDurationMinutes: '60', minAmountEnabled: false, minAmount: '', paidCancelPercent: '0', studentRates: [] })
  const [supervisorOptions, setSupervisorOptions] = useState<any[]>([])
  useEffect(() => { if (open) getColleagueOptions().then((data) => setSupervisorOptions(data.supervisors)).catch(() => setSupervisorOptions([])) }, [open])
  const [submitting, setSubmitting] = useState(false)
  const {
    enabled: franchisesEnabled,
    options: franchiseOptions,
    autoSelect,
    loading: franchisesLoading,
  } = useFranchises()
  const availableFranchiseOptions = useMemo(() => {
    if (allowedFranchiseIds === undefined) return franchiseOptions

    const allowedIds = allowedFranchiseIds.map(Number)
    return franchiseOptions.filter((option) =>
      allowedIds.includes(Number(option.value)),
    )
  }, [allowedFranchiseIds, franchiseOptions])
  const defaultFranchiseId = useMemo(() => {
    if (
      autoSelect &&
      availableFranchiseOptions.some(
        (option) => Number(option.value) === Number(autoSelect),
      )
    ) {
      return Number(autoSelect)
    }

    return availableFranchiseOptions.length === 1
      ? Number(availableFranchiseOptions[0].value)
      : null
  }, [autoSelect, availableFranchiseOptions])
  const selectedFranchiseId = Form.useWatch('franchise_id', form)

  const { isSeminarian, isAdminOrJuniorAdmin, isFranchiseManager } = useRoles()
  const canEditWeeklyLessons = isAdminOrJuniorAdmin || isFranchiseManager

  useEffect(() => {
    if (
      !open ||
      !isAdminOrJuniorAdmin ||
      form.isFieldTouched('grading_system')
    ) {
      return
    }

    form.setFieldValue('grading_system', defaultGradingSystem)
  }, [defaultGradingSystem, form, isAdminOrJuniorAdmin, open])

  const directionsState = useGetDirection(isSeminarian, {
    anySeminarian: false,
    skip:
      !open ||
      franchisesLoading ||
      (franchisesEnabled && !selectedFranchiseId),
    ...(selectedFranchiseId
      ? { franchise_ids: [selectedFranchiseId] }
      : {}),
  })

  const [searchName, setSearchName] = useState('')
  const [colleaguesState, getColleaguesState] = useAsyncFn(
    async (name?: string, franchiseId?: number | null) =>
      usersAPI.searchUsersForSelect({
        employeesOnly: 1,
        'per-page': '1000',
        ...(franchiseId ? { franchise_id: franchiseId } : {}),
        ...(name ? { name } : {}),
      }),
  )
  const [additionalTeachersState, getAdditionalTeachersState] = useAsyncFn(
    async (name?: string, franchiseId?: number | null) =>
      usersAPI.searchUsersForSelect({
        employeesOnly: 1,
        'per-page': '1000',
        ...(franchiseId ? { franchise_id: franchiseId } : {}),
        ...(name ? { name } : {}),
      }),
  )

  useEffect(() => {
    if (
      !open ||
      isSeminarian ||
      franchisesLoading ||
      (franchisesEnabled && !selectedFranchiseId)
    ) {
      return
    }

    getColleaguesState(undefined, selectedFranchiseId)
  }, [
    franchisesEnabled,
    franchisesLoading,
    getColleaguesState,
    isSeminarian,
    open,
    selectedFranchiseId,
  ])

  useEffect(() => {
    if (
      !open ||
      franchisesLoading ||
      (franchisesEnabled && !selectedFranchiseId)
    ) {
      return
    }

    getAdditionalTeachersState(undefined, selectedFranchiseId)
  }, [
    franchisesEnabled,
    franchisesLoading,
    getAdditionalTeachersState,
    open,
    selectedFranchiseId,
  ])

  useDebounce(
    () => {
      if (
        !open ||
        franchisesLoading ||
        (franchisesEnabled && !selectedFranchiseId)
      ) {
        return
      }

      if (!isSeminarian) {
        getColleaguesState(searchName || undefined, selectedFranchiseId)
      }
    },
    500,
    [
      searchName,
      open,
      franchisesEnabled,
      franchisesLoading,
      isSeminarian,
      selectedFranchiseId,
    ],
  )

  useEffect(() => {
    const currentValue = form.getFieldValue('franchise_id')
    if (
      currentValue &&
      availableFranchiseOptions.some(
        (option) => Number(option.value) === Number(currentValue),
      )
    ) {
      return
    }

    form.setFieldValue('franchise_id', defaultFranchiseId)
    form.setFieldValue('teacher_id', null)
    if (enableCourseSelect) {
      form.setFieldValue('direction_id', null)
    }
  }, [
    availableFranchiseOptions,
    defaultFranchiseId,
    enableCourseSelect,
    form,
  ])

  const seminarianId = useGetId()
  const allColleagues = colleaguesState.value?.data?.data ?? []
  const additionalTeacherOptions =
    additionalTeachersState.value?.data?.data ?? []

  useEffect(() => {
    if (!colleaguesState.error) return

    addAlert({
      title: 'Ошибка',
      text: 'Ошибка при получении списка педагогов',
      type: 'error',
    })
    onClose()
  }, [addAlert, colleaguesState.error, onClose])

  const directionIdFromForm = Form.useWatch('direction_id', form)
  const selectedTeacherId = Form.useWatch('teacher_id', form)
  const selectedDirectionId = Form.useWatch('direction_id', form)
  const applyMaxStudents = Form.useWatch('apply_max_students', form)
  const maxStudents = Form.useWatch('max_students', form)
  const name = Form.useWatch('name', form)
  const calculateTeacherSalary = Form.useWatch(
    'calculate_teacher_salary',
    form,
  )
  const lessonsPerWeek = Form.useWatch('lessons_per_week', form)
  const lessonDuration = Form.useWatch('lesson_duration', form)
  const studentLessonPriceValue = Form.useWatch('student_lesson_price', form)
  const isLessonPaymentGroupEnabled = Form.useWatch(
    'is_lesson_payment_group',
    form,
  )
  const hasAdditionalTeachers = Form.useWatch(
    'has_additional_teachers',
    form,
  )

  const directionIdToSend = enableCourseSelect
    ? directionIdFromForm
    : directionId
  const isGroupTypeFixed = !!groupType
  const shouldShowLessonPaymentGroupSwitch = !isGroupTypeFixed
  const isLessonPaymentGroup =
    groupType === 'lesson-payment' ||
    (shouldShowLessonPaymentGroupSwitch && !!isLessonPaymentGroupEnabled)
  const shouldShowSalaryFields =
    isSeminarian || !!teacherId || !!selectedTeacherId
  const teacherIdToSend = isSeminarian
    ? seminarianId
    : teacherId ?? selectedTeacherId

  const { additionalSettings, loadingAdditionalSettings } =
    useGetAdditionalSettings({ directionId: directionIdToSend })

  const applyMaxStudentsInDirection =
    !!additionalSettings?.max_students || additionalSettings?.max_students === 0

  useEffect(() => {
    if (!isLessonPaymentGroup && salaryForm.salaryType === 'per-attendee') {
      setSalaryForm((prev) => ({ ...prev, salaryType: DEFAULT_SALARY_TYPE }))
    }
  }, [isLessonPaymentGroup, salaryForm.salaryType])

  useEffect(() => {
    if (applyMaxStudentsInDirection) {
      form.setFieldsValue({
        apply_max_students: applyMaxStudentsInDirection,
        max_students: additionalSettings?.max_students,
      })
      return
    }

    form.setFieldsValue({
      apply_max_students: applyMaxStudentsInDirection,
      max_students: null,
    })
  }, [additionalSettings?.max_students, applyMaxStudentsInDirection, form])

  const [randomName, setRandomName] = useState<groupName | null>(null)
  useEffect(() => {
    if (additionalSettings?.random_group_names_in_course) {
      const randomIndex = Math.floor(
        Math.random() * additionalSettings?.groups_names?.length,
      )
      setRandomName(additionalSettings?.groups_names[randomIndex])
      form.setFieldsValue({
        name: additionalSettings?.groups_names[randomIndex]?.name ?? '',
      })
      return
    }

    setRandomName(null)
    form.setFieldsValue({
      name: '',
    })
  }, [additionalSettings, form])

  useEffect(() => {
    if (shouldShowSalaryFields) return

    form.setFieldsValue({
      calculate_teacher_salary: false,
      salary_type: DEFAULT_SALARY_TYPE,
      salary: 0,
      duration_minutes: null,
      min_amount_enabled: false,
      min_amount: null,
    })
    form.validateFields(['salary', 'duration_minutes']).catch(() => null)
  }, [form, shouldShowSalaryFields])

  const getExpandForGetUser = useMemo(() => ['roles'], [])

  const {
    profile: profileSelectedEmployee,
    loading: loadingProfileSelectedEmployee,
  } = useGetUserProfile({
    userId: selectedTeacherId,
    expand: getExpandForGetUser,
    isGetSeminarianDirections: true,
  })

  const isIncludesRoleSeminarian: boolean =
    profileSelectedEmployee?.roles?.some(
      (el) => getBaseRole(el.item_name) === rolesEnum.seminarian,
    ) ?? false
  const isChosenCourseAvailableWithChosenTeacher: boolean =
    profileSelectedEmployee?.seminarianDirections?.some(
      (el) => el.direction_id === selectedDirectionId,
    ) ?? false

  const handlerSubmit: FormProps<FieldType>['onFinish'] = (values) => {
    if (values.calculate_teacher_salary) {
      const threshold = isThresholdSalary(salaryForm.salaryType)
      if ((threshold && !isValidTeacherStudentRates(salaryForm.studentRates, true))
        || (!threshold && (salaryForm.salary === '' || !Number.isFinite(Number(salaryForm.salary)) || Number(salaryForm.salary) < 0))
        || (salaryForm.salaryType === 'per-duration' && (!Number.isInteger(Number(salaryForm.salaryDurationMinutes)) || Number(salaryForm.salaryDurationMinutes) < 1))
        || (salaryForm.salaryType === 'per-student-hour' && salaryForm.minAmountEnabled && (salaryForm.minAmount === '' || !Number.isFinite(Number(salaryForm.minAmount)) || Number(salaryForm.minAmount) < 0))
        || (isLessonPaymentGroup && !isValidPaidCancelSalaryPercent(salaryForm.paidCancelPercent))) {
        addAlert({ title: 'Проверьте ставки, длительность и возрастающий порядок порогов учащихся', type: 'error' })
        return
      }
    }
    setSubmitting(true)

    directionGroupApi
      .add({
        data: {
          direction_id: directionIdToSend,
          franchise_id: values.franchise_id,
          ...(isAdminOrJuniorAdmin ? { supervisor_ids: form.getFieldValue('supervisor_ids') ?? [], hide_journal: !!form.getFieldValue('hide_journal'), hide_homework_journal: !!form.getFieldValue('hide_homework_journal'), grading_system: getValidGradingSystem(form.getFieldValue('grading_system'), defaultGradingSystem) } : {}),
          teacher_id: teacherIdToSend,
          name: values.name,
          ...(canEditWeeklyLessons && values.lessons_per_week != null
            ? { lessons_per_week: values.lessons_per_week }
            : {}),
          ...(values.lesson_duration != null
            ? { lesson_duration: Number(values.lesson_duration) }
            : {}),
          max_students: values.apply_max_students ? values.max_students : null,
          additional_teacher_ids: values.has_additional_teachers
            ? values.additional_teacher_ids
            : [],
          suffix_type: additionalSettings?.random_group_names_in_course
            ? randomName?.suffix_type
            : null,
          ...(shouldShowSalaryFields
            ? {
                type: values.calculate_teacher_salary ? salaryForm.salaryType : DEFAULT_SALARY_TYPE,
                salary: values.calculate_teacher_salary && !isThresholdSalary(salaryForm.salaryType) ? Number(salaryForm.salary) : 0,
                student_rates: values.calculate_teacher_salary && isThresholdSalary(salaryForm.salaryType)
                  ? Object.fromEntries(salaryForm.studentRates.map((row) => [String(Number(row.count)), Number(row.salary)])) : {},
                duration_minutes: salaryForm.salaryType === 'per-duration' ? Number(salaryForm.salaryDurationMinutes) : salaryForm.salaryType === 'per-student-hour' ? 60 : null,
                min_amount_enabled: salaryForm.salaryType === 'per-student-hour' && salaryForm.minAmountEnabled,
                min_amount: salaryForm.salaryType === 'per-student-hour' && salaryForm.minAmountEnabled ? Number(salaryForm.minAmount) : null,
                ...(isLessonPaymentGroup ? { paid_cancel_percent: Number(salaryForm.paidCancelPercent) } : {}),
              }
            : {}),
          ...(isLessonPaymentGroup
            ? {
                group_type: 'lesson-payment',
                student_lesson_price: Number(values.student_lesson_price),
                is_shared_balance: !!values.is_shared_balance,
              }
            : {}),
        },
      })
      .then((res) => {
        const groupId = Number(res?.data?.data?.id)
        if (!groupId) throw new Error()

        onGroupCreated?.(res.data.data)
        onSuccess(groupId)
        const weeklyNormSaved = !canEditWeeklyLessons || values.lessons_per_week == null
          || res?.data?.data?.lessons_per_week === values.lessons_per_week
        addAlert({
          title: weeklyNormSaved ? 'Успешно' : 'Группа создана',
          text: weeklyNormSaved ? `${groupNameSingular} успешно создана`
            : 'Сервер не подтвердил сохранение количества занятий в неделю. Проверьте норму в списке групп.',
          type: weeklyNormSaved ? 'success' : 'error',
        })
        onClose()
      })
      .catch(() => {
        addAlert({
          title: 'Ошибка',
          text: `Ошибка при создании ${groupNamePlural}`,
          type: 'error',
        })
        onClose()
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <Form
      layout="vertical"
      noValidate
      form={form}
      initialValues={{
        teacher_id: teacherId ?? null,
        franchise_id: defaultFranchiseId,
        name: '',
        direction_id: directionId,
        max_students: null,
        apply_max_students: false,
        calculate_teacher_salary: false,
        salary_type: DEFAULT_SALARY_TYPE,
        salary: 0,
        paid_cancel_percent: 0,
        duration_minutes: null,
        lesson_duration: defaultLessonDuration ?? 60,
        student_lesson_price: studentLessonPrice ?? 0,
        is_lesson_payment_group: groupType === 'lesson-payment',
        is_shared_balance: false,
        grading_system: defaultGradingSystem,
        min_amount_enabled: false,
        min_amount: null,
        has_additional_teachers: false,
        additional_teacher_ids: [],
      }}
      onFinish={handlerSubmit}
      className={cn(mclasses.modalContainer, classes.modalContainer)}
    >
      <div className={cn(mclasses.modalHeader, classes.modalHeader)}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>
            Создание новой {groupNamePlural?.toLowerCase()} {isLessonPaymentGroup ? '(Оплата по занятиям)' : ''}
          </span>
        </div>
        <div className={mclasses.closeIcon} onClick={() => onClose()}>
          <IcoRemove />
        </div>
      </div>
      {franchisesLoading ? (
        <Preloader align="center" fullScreen="app" />
      ) : (
        <div
          className={cn(
            mclasses.formWrapper,
            mclasses.overflowAuto,
            classes.formWrapper,
          )}
        >
          <div>
            {franchisesEnabled && (
              <>
                <p className={mclasses.label}>Филиал</p>
                <FormItem
                  name="franchise_id"
                  rules={[{ required: true, message: 'Выберите филиал' }]}
                >
                  <CompactSelect
                    showSearch
                    className={cn(iclasses.newSelect, classes.selectField)}
                    popupClassName={iclasses.newSelectPopup}
                    placeholder="Выберите филиал"
                    options={availableFranchiseOptions}
                    loading={franchisesLoading}
                    onChange={() => {
                      if (!teacherId) {
                        form.setFieldValue('teacher_id', null)
                      }
                      if (enableCourseSelect) {
                        form.setFieldValue('direction_id', null)
                      }
                      form.setFieldValue('additional_teacher_ids', [])
                    }}
                    disabled={
                      availableFranchiseOptions.length === 1 &&
                      !!selectedFranchiseId
                    }
                    notFoundContent="Доступные филиалы не найдены"
                    filterOption={(input: string, option: any) =>
                      String(option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </FormItem>
              </>
            )}
            {enableCourseSelect && (
              <>
                <p className={mclasses.label}>Курс</p>
                <FormItem
                  name="direction_id"
                  rules={[{ required: true, message: 'Выберите курс' }]}
                >
                  <CompactSelect
                    showSearch
                    className={cn(iclasses.newSelect, classes.selectField)}
                    popupClassName={iclasses.newSelectPopup}
                    placeholder="Выберите курс"
                    options={
                      franchisesEnabled && !selectedFranchiseId
                        ? []
                        : directionsState.loading
                          ? []
                          : directionsState.select
                    }
                    disabled={franchisesEnabled && !selectedFranchiseId}
                    notFoundContent={
                      selectedFranchiseId
                        ? 'В выбранном филиале нет подключенных курсов'
                        : 'Сначала выберите филиал'
                    }
                    loading={directionsState.loading}
                    filterOption={(input: string, option: any) =>
                      String(option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </FormItem>
              </>
            )}

            <div className={classes.nameAndDuration}><div>
            <p className={mclasses.label}>
              Название {groupNamePlural?.toLowerCase()}
            </p>
            <FormItem
              name="name"
              rules={[{ required: true, message: 'Укажите название' }]}
              getValueProps={() => ({
                validate: form.getFieldError('name').length > 0,
              })}
            >
              <TooltipWrapper
                title={
                  additionalSettings?.random_group_names_in_course
                    ? 'Вы не можете изменить название, оно подставляется рандомно из возможных вариантов'
                    : ''
                }
              >
                <div
                  className={classNames({
                    [classes.wrapperInputName]:
                      additionalSettings?.random_group_names_in_course &&
                      randomName?.suffix_type === 'group_id',
                  })}
                >
                  {/*@ts-ignore*/}
                  <TextField
                      size="small"
                    className={classNames(iclasses.newInputDesign, {
                      [classes.inputName]:
                        additionalSettings?.random_group_names_in_course &&
                        randomName?.suffix_type === 'group_id',
                    })}
                    placeholder="Введите название"
                    value={name}
                    onChange={(value) => form.setFieldValue('name', value)}
                    disabled={additionalSettings?.random_group_names_in_course}
                  />
                  {additionalSettings?.random_group_names_in_course &&
                    randomName?.suffix_type === 'group_id' && (
                      <div className={classes.newGroupNameId}>: {`{id}`}</div>
                    )}
                </div>
              </TooltipWrapper>
            </FormItem>

            </div>
                <div className={cn(classes.twoColumns, {
                  [classes.singleColumn]: !canEditWeeklyLessons,
                })}>
            {canEditWeeklyLessons && (
              <div>
                <p className={mclasses.label}>Количество занятий в неделю</p>
                <FormItem
                  name="lessons_per_week"
                  rules={[{ validator: (_, value) => value == null || (Number.isSafeInteger(value) && value >= 0)
                    ? Promise.resolve() : Promise.reject(new Error('Введите целое неотрицательное число')) }]}
                >
                  {/*@ts-ignore*/}
                  <InputWhite
                    onChange={(value: number | string) => {
                      const normalizedValue = normalizeNonNegativeNumberInput(
                        String(value ?? ''),
                      )
                      form.setFieldValue(
                        'lessons_per_week',
                        normalizedValue === ''
                          ? null
                          : Number(normalizedValue),
                      )
                    }}
                    className={classes.inputWhite}
                    value={lessonsPerWeek}
                    placeholder="Не задано"
                    type="number"
                    max={String(Number.MAX_SAFE_INTEGER)}
                  />
                </FormItem>
              </div>
            )}

                <div>
                  <p className={cn(mclasses.label, classes.fieldLabel)}>
                    Длительность занятия (СДЗ), минуты
                    <TooltipWrapper title="Данное поле отвечает за стандартную длительность занятий в данной группе. Вы можете создавать занятие другой длительности, но стандартное значение необходимо для удобства работы.">
                      <span className={classes.infoIcon}>
                        <InfoIcon />
                      </span>
                    </TooltipWrapper>
                  </p>
                <FormItem
                  name="lesson_duration"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value === null || value === undefined) {
                          if (!isLessonPaymentGroup) return Promise.resolve()
                          return Promise.reject(
                            new Error('Укажите стандартное время занятия'),
                          )
                        }

                        return Number(value) > 0
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                'Укажите корректное стандартное время занятия',
                              ),
                            )
                      },
                    },
                  ]}
                >
                  {/*@ts-ignore*/}
                  <InputWhite
                    onChange={(value: number | string) => {
                      const normalizedValue = normalizeNonNegativeNumberInput(
                        String(value ?? ''),
                      )
                      form.setFieldsValue({
                        lesson_duration:
                          normalizedValue === ''
                            ? null
                            : Number(normalizedValue),
                      })
                    }}
                    className={classes.inputWhite}
                    value={lessonDuration}
                    placeholder="Введите время"
                    type="number"
                  />
                </FormItem>
                </div>
                {isLessonPaymentGroup && <FormItem
                  className={classes.lessonPriceField}
                  name="student_lesson_price"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value === null || value === undefined) {
                          return Promise.reject(
                            new Error('Укажите стоимость урока'),
                          )
                        }

                        return Number(value) >= 0
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error('Укажите корректную стоимость урока'),
                            )
                      },
                    },
                  ]}
                >
                  {/*@ts-ignore*/}
                  <InputWhite
                    onChange={(value: number | string) => {
                      const normalizedValue = normalizeNonNegativeNumberInput(
                        String(value ?? ''),
                      )
                      form.setFieldsValue({
                        student_lesson_price:
                          normalizedValue === ''
                            ? null
                            : Number(normalizedValue),
                      })
                    }}
                    className={classes.inputWhite}
                    value={studentLessonPriceValue}
                    placeholder="Введите стоимость"
                    type="number"
                    title="Стоимость урока (для ученика)"
                    max="1000000"
                  />
                </FormItem>}
                </div>
            </div>
            {!isSeminarian && !teacherId && (
              <>
                <p className={mclasses.label}>Педагог</p>
                <FormItem
                  name="teacher_id"
                  rules={[{ required: true, message: 'Выберите педагога' }]}
                >
                  <CompactSelect
                    showSearch
                    className={cn(iclasses.newSelect, classes.selectField)}
                    popupClassName={iclasses.newSelectPopup}
                    placeholder="Выберите педагога"
                    options={colleaguesState.loading ? [] : allColleagues}
                    loading={colleaguesState.loading}
                    disabled={franchisesEnabled && !selectedFranchiseId}
                    notFoundContent={
                      franchisesEnabled
                        ? 'В данной франшизе нет педагогов'
                        : 'Педагоги не найдены'
                    }
                    onSearch={(value: string) => setSearchName(value)}
                    onBlur={() => setSearchName('')}
                    filterOption={false}
                  />
                </FormItem>
                {loadingProfileSelectedEmployee ? (
                  <Preloader align="center" />
                ) : !isIncludesRoleSeminarian && !!selectedTeacherId ? (
                  <Text
                    className={classes.warningText}
                    texts={[
                      `У выбранного сотрудника нет роли педагога, она добавится ему автоматически при добавлении ему этой ${groupNamePlural?.toLowerCase()}`,
                    ]}
                  />
                ) : !isChosenCourseAvailableWithChosenTeacher &&
                  !!selectedTeacherId &&
                  !!selectedDirectionId ? (
                  <Text
                    className={classes.warningText}
                    texts={[
                      'У этого сотрудника нет доступа к этому курсу. При создании с ним группы система автоматически добавит ему доступ к курсу.',
                    ]}
                  />
                ) : null}
              </>
            )}

            {shouldShowLessonPaymentGroupSwitch && (
              <div className={classes.switchWrapper}>
                <FormItem name="is_lesson_payment_group" valuePropName="checked">
                  <div className={classes.switch}>
                    <Switch
                      checked={isLessonPaymentGroupEnabled || false}
                      onChange={(checked) => {
                        form.setFieldsValue({
                          is_lesson_payment_group: checked,
                        })
                      }}
                    />
                    <div>Группа с оплатой по занятиям</div><TooltipWrapper title="Есть два типа групп: обычные — для потоковых тарифов; с оплатой по занятиям — с отметками посещения и списанием за каждое занятие."><InfoIcon /></TooltipWrapper>
                  </div>
                </FormItem>
              </div>
            )}

            <details className={classes.section}><summary>Дополнительно</summary>
            <div className={classes.switchWrapper}>
              <FormItem name="apply_max_students" valuePropName="checked">
                <div className={classes.switch}>
                  <TooltipWrapper
                    title={
                      applyMaxStudentsInDirection
                        ? 'Вы не можете изменить это значение, оно настроено для всех групп выбранного курса'
                        : ''
                    }
                  >
                    <Switch
                      checked={applyMaxStudents || false}
                      disabled={
                        applyMaxStudentsInDirection || loadingAdditionalSettings
                      }
                      onChange={(checked) => {
                        form.setFieldsValue({
                          apply_max_students: checked,
                        })
                      }}
                    />
                  </TooltipWrapper>
                  <div>Ограничить количество учащихся</div>
                  <TooltipWrapper
                    title={`Для ${groupNamePlural?.toLowerCase()} можно задать максимальное число учащихся. Когда это число будет достигнуто, система не будет позволять добавлять новых учащихся`}
                  >
                    <InfoIcon />
                  </TooltipWrapper>
                </div>
              </FormItem>
            </div>

            {applyMaxStudents && (
              <FormItem
                name="max_students"
                rules={[
                  {
                    required: true,
                    message: `Укажите максимальное количество учащихся для ${groupNamePlural?.toLowerCase()}`,
                  },
                ]}
              >
                <TooltipWrapper
                  title={
                    applyMaxStudentsInDirection
                      ? 'Вы не можете изменить это значение, оно настроено для всех групп выбранного курса'
                      : ''
                  }
                >
                  <div>
                    {/*@ts-ignore*/}
                    <InputWhite
                      onChange={(value: number | string) => {
                        form.setFieldsValue({
                          max_students: value,
                        })
                      }}
                      disabled={
                        applyMaxStudentsInDirection || loadingAdditionalSettings
                      }
                      className={classes.inputWhite}
                      value={maxStudents}
                      max="10000"
                      placeholder="Введите количество"
                      type="number"
                      title="Максимальное количество учащихся"
                    />
                  </div>
                </TooltipWrapper>
              </FormItem>
            )}
            <div className={classes.switchWrapper}>
              <div className={classes.switch}>
                <FormItem
                  name="has_additional_teachers"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch
                    onChange={(checked) => {
                      if (!checked) {
                        form.setFieldValue('additional_teacher_ids', [])
                      }
                    }}
                  />
                </FormItem>
                <div>У данной группы есть дополнительные педагоги</div>
                <TooltipWrapper title={ADDITIONAL_TEACHERS_TOOLTIP}>
                  <InfoIcon />
                </TooltipWrapper>
              </div>
            </div>

            {hasAdditionalTeachers && (
              <>
                <p className={mclasses.label}>Дополнительные педагоги</p>
                <FormItem
                  name="additional_teacher_ids"
                  rules={[
                    {
                      required: true,
                      message: 'Выберите хотя бы одного дополнительного педагога',
                    },
                  ]}
                >
                  <CompactSelect
                    mode="multiple"
                    showSearch
                    className={classes.selectField}
                    placeholder="Выберите педагогов"
                    options={additionalTeacherOptions.filter(
                      (option: { value: number | string }) =>
                        Number(option.value) !== Number(teacherIdToSend),
                    )}
                    loading={additionalTeachersState.loading}
                    disabled={franchisesEnabled && !selectedFranchiseId}
                  />
                </FormItem>
              </>
            )}

            {isLessonPaymentGroup && (
              <>
                <div className={classes.switchWrapper}>
                  <div className={classes.switch}>
                    <FormItem name="is_shared_balance" valuePropName="checked" noStyle>
                      <Switch />
                    </FormItem>
                    <span>У данной группы один баланс на всех учащихся</span>
                    <TooltipWrapper title="Если включить данный чекбокс, то у всех учащихся данной группы будет один общий баланс. Это позволит одному учащемуся (или родителю) платить сразу за всех">
                      <InfoIcon />
                    </TooltipWrapper>
                  </div>
                </div>
              </>
            )}

              {isAdminOrJuniorAdmin && <FormItem
                name="supervisor_ids"
                label={(
                  <span className={classes.fieldLabel}>
                    Классный руководитель / Куратор
                    <TooltipWrapper title={'Вы можете выбрать несколько кураторов из числа сотрудников с ролью "методист", данные сотрудники смогут видеть аналитику по данной группе, Эл.Жур, задания и многое другое.'}>
                      <span className={classes.infoIcon}>
                        <InfoIcon />
                      </span>
                    </TooltipWrapper>
                  </span>
                )}
              ><CompactSelect mode="multiple" options={supervisorOptions} placeholder="Выберите сотрудников" /></FormItem>}
            </details>
            {shouldShowSalaryFields && <details className={classes.section}>
              <summary>ЗП педагога</summary>
              <FormItem name="calculate_teacher_salary" valuePropName="checked"><div className={classes.switch}>
                <Switch checked={!!calculateTeacherSalary} onChange={(checked) => form.setFieldValue('calculate_teacher_salary', checked)} />
                <span>Рассчитывать зарплату за проведённые занятия</span>
              </div></FormItem>
              {calculateTeacherSalary && <TeacherSalaryFields lessonPayment={isLessonPaymentGroup} value={salaryForm} onChange={(update) => setSalaryForm((prev) => ({ ...prev, ...update }))} />}
            </details>}
            {isAdminOrJuniorAdmin && <details className={classes.section}><summary>Настройки эл. журнала для группы</summary>
              <div className={classes.switch}>
                <FormItem name="hide_journal" valuePropName="checked" noStyle><Switch /></FormItem>
                <span>Скрыть группу в основном разделе Эл.Жура</span>
                <TooltipWrapper title={HIDE_JOURNAL_TOOLTIP}><InfoIcon /></TooltipWrapper>
              </div>
              <div className={classes.switch}>
                <FormItem name="hide_homework_journal" valuePropName="checked" noStyle><Switch /></FormItem>
                <span>Скрыть группу в разделе ДЗ/КР Эл.Жура</span>
                <TooltipWrapper title={HIDE_HOMEWORK_JOURNAL_TOOLTIP}><InfoIcon /></TooltipWrapper>
              </div>
              <FormItem name="grading_system" label="Система оценивания" rules={[{ required: true, message: 'Выберите систему оценивания' }]}><CompactSelect options={GRADING_SYSTEM_OPTIONS} /></FormItem>
            </details>}
          </div>
        </div>
      )}
      <div className={mclasses.actions}>
        <Button color="gray" onClick={() => onClose()}>
          Отменить
        </Button>
        <Button
          type="submit"
          color="primary"
          disabled={
            submitting ||
            colleaguesState.loading ||
            directionsState.loading ||
            loadingAdditionalSettings
          }
          isLoading={submitting}
        >
          Подтвердить
        </Button>
      </div>
    </Form>
  )
}

const CreateGroupModal: FC<CreateGroupProps> = (props) => {
  const { open, onClose } = props

  return (
    <Modal
      active={open}
      className={classNames(mclasses.modalWrapper, classes.mwrapper) as any}
      setActive={() => onClose()}
    >
      <CreateGroupModalContent {...props} />
    </Modal>
  )
}

export default CreateGroupModal
